import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import participantsData from '../data/participants.json';
import reactParticipantsData from '../data/react-participants.json';
import nestjsParticipantsData from '../data/nestjs-participants.json';
import MetaService from '../services/metaService';
import Celebration from './Celebration';

const Leaderboard = () => {
  const [isReactPodium, setIsReactPodium] = useState(false);
  const [isNestjsPodium, setIsNestjsPodium] = useState(true);
  const [showCelebration, setShowCelebration] = useState(false);
  // const [showRickRoll, setShowRickRoll] = useState(false);
  const celebrationDurationMs = 6000; // keep celebration timing in one place
  const [isMobile, setIsMobile] = useState(false);

  const getCurrentData = () => {
    if (isNestjsPodium) return nestjsParticipantsData;
    if (isReactPodium) return reactParticipantsData;
    return participantsData;
  };

  const currentData = getCurrentData();

  // Update meta tags when component mounts or mode changes
  useEffect(() => {
    MetaService.updateMetaTags(
      participantsData,
      reactParticipantsData,
      isReactPodium
    );
    MetaService.generateStructuredData(
      participantsData,
      reactParticipantsData,
      isReactPodium
    );
    MetaService.updateCanonicalUrl();
    MetaService.addShareableUrl();
  }, [isReactPodium, isNestjsPodium]);

  // Trigger celebration only once on initial mount (hidden by default)
  useEffect(() => {
    setShowCelebration(false);
    // Small buffer so fireworks sparks can finish gracefully
    const timer = setTimeout(
      () => setShowCelebration(false),
      celebrationDurationMs + 800
    );
    return () => clearTimeout(timer);
  }, []);

  // Detect mobile devices to adjust celebration mode
  useEffect(() => {
    const computeIsMobile = () => {
      if (typeof window === 'undefined' || typeof navigator === 'undefined')
        return false;
      const smallScreen =
        window.matchMedia && window.matchMedia('(max-width: 768px)').matches;
      const uaMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(
        navigator.userAgent
      );
      return smallScreen || uaMobile;
    };
    setIsMobile(computeIsMobile());
    const onResize = () => setIsMobile(computeIsMobile());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const { participants } = currentData;

  // Use participants in their original JSON order
  const sortedParticipants = participants;

  // Get top 3 participants for podium
  const topThree = sortedParticipants.slice(0, 3);

  // Define podium data with automatic rank assignment
  const leaders = topThree.map((participant, index) => {
    if (isNestjsPodium) {
      return {
        rank: index + 1,
        name: participant.name,
        problems: [127, 98, 87][index],
        medal: ['🥇', '🥈', '🥉'][index],
        color: ['#E0234E', '#000000', '#ED1F41'][index],
        rankClass: ['nestjs-first', 'nestjs-second', 'nestjs-third'][index],
      };
    }
    if (isReactPodium) {
      return {
        rank: index + 1,
        name: participant.name,
        problems: [127, 98, 87][index],
        medal: ['⚛️', '⚛️', '⚛️'][index],
        color: ['#61DAFB', '#21232A', '#282C34'][index],
        rankClass: ['react-first', 'react-second', 'react-third'][index],
      };
    }
    return {
      rank: index + 1,
      name: participant.name,
      problems: [127, 98, 87][index],
      medal: ['🥇', '🥈', '🥉'][index],
      color: ['#FFD700', '#C0C0C0', '#CD7F32'][index],
      rankClass: ['gold', 'silver', 'bronze'][index],
    };
  });

  const getPageTitle = () => {
    if (isNestjsPodium)
      return 'NestJS Masters - Tech Hub Leaderboard';
    if (isReactPodium) return 'React Champions - Tech Hub Leaderboard';
    return 'Top Problem Solvers - Tech Hub Leaderboard';
  };

  const getPageDescription = () => {
    if (isNestjsPodium)
      return 'Discover our NestJS development experts and masters at Tech Hub. Excellence in NestJS backend development and architecture.';
    if (isReactPodium)
      return 'Discover our React development champions and masters at Tech Hub. Excellence in React development and innovation.';
    return 'Discover our top problem solvers and dedicated community members at Tech Hub. Excellence in problem solving and innovation.';
  };

  const pageTitle = getPageTitle();
  const pageDescription = getPageDescription();
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const imageUrl = `${currentUrl}/logo192.png`;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name='description' content={pageDescription} />

        {/* Open Graph tags for Facebook, LinkedIn, etc. */}
        <meta property='og:title' content={pageTitle} />
        <meta property='og:description' content={pageDescription} />
        <meta property='og:type' content='website' />
        <meta property='og:url' content={currentUrl} />
        <meta property='og:image' content={imageUrl} />
        <meta property='og:image:width' content='1200' />
        <meta property='og:image:height' content='630' />
        <meta property='og:site_name' content='Tech Hub Leaderboard' />

        {/* Twitter Card tags */}
        <meta name='twitter:card' content='summary_large_image' />
        <meta name='twitter:title' content={pageTitle} />
        <meta name='twitter:description' content={pageDescription} />
        <meta name='twitter:image' content={imageUrl} />

        {/* Additional meta tags */}
        <meta name='robots' content='index, follow' />
        <meta name='author' content='Tech Hub' />
        <link rel='canonical' href={currentUrl} />
      </Helmet>

      <div className='leaderboard-container'>
        <div className='leaderboard-header'>
          <h2
            className={
              isNestjsPodium
                ? 'nestjs-title'
                : isReactPodium
                ? 'react-title'
                : ''
            }
          >
            🏆{' '}
            {isNestjsPodium
              ? 'NestJS Masters'
              : isReactPodium
              ? 'React Champions'
              : 'Top Problem Solvers'}
          </h2>
          <p>
            {isNestjsPodium
              ? 'Our NestJS development experts'
              : isReactPodium
              ? 'Our React development masters'
              : 'Our most dedicated community members'}
          </p>

          <div className='podium-switch'>
            <label className='switch'>
              <input
                type='checkbox'
                checked={isNestjsPodium}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  setIsNestjsPodium(isChecked);
                  setIsReactPodium(false);
                }}
              />
              <span className='slider'>
                <span className='switch-label left'>General</span>
                <span className='switch-label right'>NestJS</span>
              </span>
            </label>
          </div>
        </div>

        <div className='podium'>
          {/* First Place */}
          <div className='podium-position first'>
            <div
              className={`podium-card winner ${
                isNestjsPodium
                  ? 'nestjs-winner'
                  : isReactPodium
                  ? 'react-winner'
                  : ''
              }`}
            >
              <div className='winner-crown'>
                {isNestjsPodium ? '👑' : isReactPodium ? '⚛️' : '👑'}
              </div>
              <div className={`rank-badge ${leaders[0].rankClass}`}>
                <span className='rank-number'>1</span>
              </div>
              <div className='leader-info'>
                <h3>{leaders[0].name}</h3>
                {/* <p className='problems-count'>{leaders[0].problems} problems</p> */}
              </div>
              <div className='medal'>{leaders[0].medal}</div>
            </div>
          </div>

          {/* Second Place */}
          <div className='podium-position second'>
            <div
              className={`podium-card ${
                isNestjsPodium
                  ? 'nestjs-card'
                  : isReactPodium
                  ? 'react-card'
                  : ''
              }`}
            >
              <div className={`rank-badge ${leaders[1].rankClass}`}>
                <span className='rank-number'>2</span>
              </div>
              <div className='leader-info'>
                <h3>{leaders[1].name}</h3>
                {/* <p className='problems-count'>{leaders[1].problems} Points</p> */}
              </div>
              <div className='medal'>{leaders[1].medal}</div>
            </div>
          </div>

          {/* Third Place */}
          <div className='podium-position third'>
            <div
              className={`podium-card ${
                isNestjsPodium
                  ? 'nestjs-card'
                  : isReactPodium
                  ? 'react-card'
                  : ''
              }`}
            >
              <div className={`rank-badge ${leaders[2].rankClass}`}>
                <span className='rank-number'>3</span>
              </div>
              <div className='leader-info'>
                <h3>{leaders[2].name}</h3>
                {/* <p className='problems-count'>{leaders[2].problems} problems</p> */}
              </div>
              <div className='medal'>{leaders[2].medal}</div>
            </div>
          </div>
        </div>

        <div className='other-participants'>
          <h3
            className={
              isNestjsPodium
                ? 'nestjs-participants-title'
                : isReactPodium
                ? 'react-participants-title'
                : ''
            }
          >
            🌟 All Participants
          </h3>
          <div className='participants-grid'>
            {sortedParticipants.map((participant, index) => {
              const isTopThree = index < 3;
              const isTopTen = index < 10;
              const rankPosition = index + 1;

              let rankClass = '';
              let medal = '';

              if (isTopThree) {
                if (isNestjsPodium) {
                  rankClass = ['nestjs-first', 'nestjs-second', 'nestjs-third'][index];
                  medal = ['🥇', '🥈', '🥉'][index];
                } else if (isReactPodium) {
                  rankClass = ['react-first', 'react-second', 'react-third'][
                    index
                  ];
                  medal = ['⚛️', '⚛️', '⚛️'][index];
                } else {
                  rankClass = ['gold', 'silver', 'bronze'][index];
                  medal = ['🥇', '🥈', '🥉'][index];
                }
              } else if (isTopTen) {
                if (isNestjsPodium) {
                  rankClass = 'nestjs-top-ten';
                  medal = '⭐';
                } else if (isReactPodium) {
                  rankClass = 'react-top-ten';
                  medal = '⚛️';
                } else {
                  rankClass = 'top-ten';
                  medal = '⭐';
                }
              }

              // Check if participant is in danger zone (last 5)
              const totalParticipants = sortedParticipants.length;
              const isDangerZone = index >= totalParticipants - 5;

              if (isDangerZone && !isTopThree && !isTopTen) {
                if (isNestjsPodium) {
                  rankClass = 'nestjs-danger-zone';
                  medal = '⚠️';
                } else if (isReactPodium) {
                  rankClass = 'react-danger-zone';
                  medal = '⚠️';
                } else {
                  rankClass = 'danger-zone';
                  medal = '⚠️';
                }
              }

              return (
                <div
                  key={participant.sortId}
                  className={`participant-card ${rankClass} ${
                    isNestjsPodium &&
                    !isTopThree &&
                    !isTopTen &&
                    !isDangerZone
                      ? 'nestjs-regular'
                      : isReactPodium && !isTopThree && !isTopTen
                      ? 'react-regular'
                      : ''
                  }`}
                  style={{ '--delay': `${index * 0.1}s` }}
                  data-rank={rankPosition}
                  data-rank-class={rankClass}
                >
                  {(isTopThree || isTopTen || isDangerZone) && (
                    <div className='participant-rank'>
                      <span className='rank-number'>{rankPosition}</span>
                      <span className='rank-medal'>{medal}</span>
                    </div>
                  )}
                  <span className='participant-name'>{participant.name}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className='achievement-message'>
          <p
            className={
              isNestjsPodium
                ? 'nestjs-achievement-title'
                : isReactPodium
                ? 'react-achievement-title'
                : ''
            }
          >
            🎯{' '}
            {isNestjsPodium
              ? 'Excellence in NestJS Development'
              : isReactPodium
              ? 'Excellence in React Development'
              : 'Excellence in Problem Solving'}
          </p>
          <p>
            {isNestjsPodium
              ? 'Keep building amazing NestJS applications!'
              : isReactPodium
              ? 'Keep building amazing React applications!'
              : 'Keep pushing boundaries and inspiring others!'}
          </p>
        </div>
      </div>
      {showCelebration && (
        <Celebration
          active={showCelebration}
          durationMs={celebrationDurationMs}
          intensity={160}
          mode={isMobile ? 'side-cannons' : 'fireworks'}
        />
      )}
    </>
  );
};

export default Leaderboard;
